// /home/edgelexllc/projects/edgelexapp/webelieve-frontend/src/features/wordprocessing/formatting/toolbar/EdgeDocsToolbar.tsx
/**
 * EdgeDocs Toolbar - MS Word-like formatting toolbar
 * 
 * Architecture (2026-01-24):
 * - Uses PreviewableDropdown components for live preview on hover
 * - Selection is bound when user starts interacting with any control
 * - Preview changes are shown immediately, committed only on click
 * - Escape or click-away reverts to original value
 * 
 * Key Features:
 * - Font size dropdown with live preview
 * - Line spacing dropdown with live preview  
 * - Font family cascading dropdown
 * - Bold/Italic/Underline toggles
 * - Zoom controls
 * - Undo/Redo buttons
 */
import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import { FileTypeIcon, getFileTypeFromExtension, type FileType } from "../../rendering/icons/FileTypeIcon";
import type { DocumentFontType } from "../../editors/edgedocs/EdgeDocsTypes";
import { getEnabledFontFamilies, type FontFamily } from "../../services/FontAPI";
import { CascadingFontDropdown } from "./CascadingFontDropdown";
import { DocumentLogger } from "../../../../utils/loggers/DocumentLogger";
import { ToolbarProvider, useToolbarContextSafe, type ParagraphRange } from "./ToolbarContext";
import { FontSizeDropdown, LineSpacingDropdown } from "./PreviewableDropdown";
import searchIcon from "../../rendering/icons/search.svg";
import printIcon from "../../rendering/icons/print.svg";
import paintIcon from "../../rendering/icons/paint.svg";

type SmoothSelectProps = {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  /** e.g., "w-[150px]" or responsive widths like "w-[140px] sm:w-[160px]" */
  triggerWidth?: string;
  ariaLabel?: string;
  menuFit?: boolean;          // true → fit to content (narrow lists like font sizes)
  renderItem?: (opt: string) => React.ReactNode; // to preview styles at different sizes
};

export function SmoothSelect({
  value,
  onChange,
  options,
  triggerWidth = "w-[140px]",
  ariaLabel,
  menuFit = false,
  renderItem,
}: SmoothSelectProps) {
  return (
    <RadixSelect.Root value={value} onValueChange={onChange}>
      <RadixSelect.Trigger
        aria-label={ariaLabel || value}
        className={`edg-trigger inline-flex h-7 items-center justify-between rounded px-2 text-[13px] text-gray-900 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10 ${triggerWidth}`}
      >
        {/* Single line, ellipsized label */}
        <span className="edg-trigger-text flex-1 min-w-0 text-gray-900 dark:text-slate-100">{value}</span>
        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-70" />
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={6}
          className={`edg-dd z-[1000] rounded-md bg-white dark:bg-slate-800 ring-1 ring-black/10 ${menuFit ? "edg-dd--fit" : "edg-dd--match"}`}
        >
          <RadixSelect.Viewport className="edg-dd-viewport">
            {options.map((opt) => (
              <RadixSelect.Item key={opt} value={opt} className="edg-dd-item text-[13px] text-gray-900 dark:text-slate-100 outline-none">
                <RadixSelect.ItemText>
                  {renderItem ? renderItem(opt) : opt}
                </RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

/** Google Docs–like fixed heights */
export const APPBAR_H = 60;   // title + menubar + share + spacing
export const TOOLBAR_H = 34;  // compact formatting toolbar row
export const RULER_H  = 24;   // ruler stub (non-functional)
export const EDGEDOCS_TOP_OFFSET = APPBAR_H + TOOLBAR_H + RULER_H;


type Props = {
  /** Width of the left sidebar (px). We pin the toolbar immediately to its right. */
  sidebarWidth: number;
  /** Callback to open the page setup dialog */
  onOpenPageSetup?: () => void;
  /** Callback to save the document */
  onSave?: () => void;

  /** Actual document title from DMS API */
  documentTitle?: string;     // e.g., "700AMNewDoc" (from dms_documents.title)
  versionNumber?: string;     // e.g., "v0.0" (from dms_document_versions.version_number)
  saveStatus?: 'saved' | 'saving' | 'error';

  /** WASM typesetter status */
  wasmStatus?: 'ready' | 'loading' | 'error';

  // Controlled typography
  fontFamilyKey: DocumentFontType;                         // e.g., 'serif' | 'sans' | 'mono'
  // 🔧 FIX (2026-01-21): onChangeFontFamily now accepts optional weight/style to avoid race condition
  // When user selects "Inter Bold", we need family="Inter", weight=700, style="normal" all at once
  onChangeFontFamily: (key: DocumentFontType, weight?: number, style?: 'normal' | 'italic' | 'oblique') => void;
  onChangeFontWeight?: (weight: number) => void;           // ✅ NEW: Font weight callback
  onChangeFontStyle?: (style: 'normal' | 'italic' | 'oblique') => void;  // ✅ NEW: Font style callback

  fontSizePx: number;                                      // world px @ 96dpi
  onChangeFontSize: (px: number) => void;
  
  // 🆕 MS Word-like preview callbacks (2026-01-24)
  // These are called with paragraph ranges so formatting can be applied to selected text
  onChangeFontSizeWithRanges?: (px: number, ranges: ParagraphRange[]) => Promise<void>;
  onPreviewFontSize?: (px: number, ranges: ParagraphRange[]) => Promise<void>;
  onCancelFontSizePreview?: (originalPx: number, ranges: ParagraphRange[]) => Promise<void>;

  lineHeightMul: number;                                   // e.g., 1.2–1.6
  onChangeLineHeight?: (mul: number) => void;              // Document-level line spacing change
  onChangeParagraphLineSpacing?: (mul: number) => void;    // Per-paragraph line spacing (2026-01-19)
  
  // 🆕 MS Word-like preview callbacks for line spacing (2026-01-24)
  onChangeParagraphLineSpacingWithRanges?: (mul: number, ranges: ParagraphRange[]) => Promise<void>;
  onPreviewLineSpacing?: (mul: number, ranges: ParagraphRange[]) => Promise<void>;
  onCancelLineSpacingPreview?: (originalMul: number, ranges: ParagraphRange[]) => Promise<void>;

  // Controlled zoom
  zoomMode: 'fit' | 'fit-width' | 'custom';  // ✅ Enhancement 4: Added 'fit-width' mode
  zoomValue: number;                                       // 0.25..4.0 (only used when zoomMode === 'custom')
  onChangeZoomMode: (m: 'fit' | 'fit-width' | 'custom') => void;
  onChangeZoomValue: (z: number) => void;

  // Undo/Redo callbacks
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // ✅ Phase E (2026-01-21): Character-level formatting callbacks
  onToggleBold?: () => void;
  onToggleItalic?: () => void;
  onToggleUnderline?: () => void;
  onClearFormatting?: () => void;

  // ✅ Phase E (2026-01-21): Current formatting state for toolbar button highlights
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  
  // 🆕 Selection getters for ToolbarContext (2026-01-24)
  // These are passed from DocumentOrchestrator to enable selection-based formatting
  getSelection?: () => { anchor: { logical?: { paraIndex: number; charOffset: number } }; focus: { logical?: { paraIndex: number; charOffset: number } }; isCollapsed: boolean } | null;
  getDocumentState?: () => { paragraphs?: Array<{ id: string; textStart: number; textEnd: number }> } | null;
};

function Menubar({ onOpenPageSetup, onSave }: { 
  onOpenPageSetup?: () => void;
  onSave?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button
        className="rounded px-1 py-1 hover:bg-black/5 dark:hover:bg-white/10"
        onClick={() => setOpen((v) => !v)}
      >
        File
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 w-56 overflow-hidden rounded-md border bg-white dark:bg-slate-800 shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <button
            className="block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700"
            onClick={() => { 
              setOpen(false); 
              onSave?.(); 
            }}
          >
            <div className="flex items-center justify-between">
              <span>Save</span>
              <span className="text-xs text-gray-500">Ctrl+S</span>
            </div>
          </button>
          <div className="h-px bg-gray-200 dark:bg-slate-600" />
          <button
            className="block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700"
            onClick={() => { setOpen(false); onOpenPageSetup?.(); }}
          >
            Page setup…
          </button>
          <div className="h-px bg-gray-200 dark:bg-slate-600" />
          <button className="block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700" onClick={() => setOpen(false)}>
            Print preview
          </button>
        </div>
      )}
    </div>
  );
}

export default function EdgeDocsToolbar({
  sidebarWidth,
  onOpenPageSetup,
  onSave,
  documentTitle,
  versionNumber,
  saveStatus,
  wasmStatus = 'loading',
  fontFamilyKey,
  onChangeFontFamily,
  onChangeFontWeight,  // ✅ NEW: Destructure weight callback
  onChangeFontStyle,   // ✅ NEW: Destructure style callback
  fontSizePx,
  onChangeFontSize,
  // 🆕 MS Word-like preview callbacks (2026-01-24)
  onChangeFontSizeWithRanges,
  onPreviewFontSize,
  onCancelFontSizePreview,
  lineHeightMul,
  onChangeLineHeight,
  onChangeParagraphLineSpacing,  // 2026-01-19: Per-paragraph line spacing
  // 🆕 MS Word-like preview callbacks for line spacing (2026-01-24)
  onChangeParagraphLineSpacingWithRanges,
  onPreviewLineSpacing,
  onCancelLineSpacingPreview,
  zoomMode,
  zoomValue,
  onChangeZoomMode,
  onChangeZoomValue,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  // ✅ Phase E (2026-01-21): Character-level formatting
  onToggleBold,
  onToggleItalic,
  onToggleUnderline,
  onClearFormatting,
  isBold = false,
  isItalic = false,
  isUnderline = false,
  // 🆕 Selection getters (2026-01-24)
  getSelection,
  getDocumentState,
}: Props) {
  // 🔬 DIAGNOSTIC: Log received preview callbacks
  React.useEffect(() => {
    console.log('[EdgeDocsToolbar] Preview callback props:', {
      hasOnChangeFontSizeWithRanges: !!onChangeFontSizeWithRanges,
      hasOnPreviewFontSize: !!onPreviewFontSize,
      hasOnCancelFontSizePreview: !!onCancelFontSizePreview,
      hasGetSelection: !!getSelection,
      hasGetDocumentState: !!getDocumentState
    });
  }, [onChangeFontSizeWithRanges, onPreviewFontSize, onCancelFontSizePreview, getSelection, getDocumentState]);

  // Document state - derive file type from document title extension
  const documentFileType = React.useMemo(() => {
    if (!documentTitle) return undefined;
    const extension = documentTitle.split('.').pop()?.toLowerCase();
    return extension ? getFileTypeFromExtension(extension) : undefined;
  }, [documentTitle]);
  
  // Alignment constant: icon(lg ~24px) + gap-2 (~8px) = 32px
  const TITLE_ICON_OFFSET_PX = 32;


  // Zoom parsing and constants
  const ZOOMS = ["50%","75%","90%","100%","125%","150%","200%"];

  function parsePercent(s: string): number {
    const n = Number(s.replace('%','').trim());
    return isFinite(n) && n > 0 ? n / 100 : 1;
  }

  // =========================================================================
  // DYNAMIC FONT LOADING FROM API (Google Docs-style cascading dropdown)
  // =========================================================================
  const [fontFamilies, setFontFamilies] = React.useState<FontFamily[]>([]);
  const [fontsLoading, setFontsLoading] = React.useState(true);
  
  // Track selected font weight and style (for variant selection)
  const [selectedFontWeight, setSelectedFontWeight] = React.useState<number>(400);
  const [selectedFontStyle, setSelectedFontStyle] = React.useState<string>('normal');

  // Load enabled fonts grouped by family from API on mount
  React.useEffect(() => {
    let mounted = true;

    const loadFonts = async () => {
      try {
        setFontsLoading(true);
        const families = await getEnabledFontFamilies();
        
        if (mounted) {
          setFontFamilies(families);
          console.log(`[EdgeDocsToolbar] Loaded ${families.length} font families from Font Manager`);
        }
      } catch (error) {
        console.error('[EdgeDocsToolbar] Failed to load fonts:', error);
        // Keep empty array - will show "No fonts available" message
      } finally {
        if (mounted) {
          setFontsLoading(false);
        }
      }
    };

    loadFonts();

    return () => {
      mounted = false;
    };
  }, []);

  // Handle font family + variant selection
  const handleFontSelect = (family: string, weight: number, style: string) => {
    setSelectedFontWeight(weight);
    setSelectedFontStyle(style);

    // 🔧 FIX (2026-01-21): Pass weight and style together with family to avoid race condition
    // This ensures the orchestrator resolves font_uid with the correct weight/style
    onChangeFontFamily(family as DocumentFontType, weight, style as 'normal' | 'italic' | 'oblique');
    onChangeFontWeight?.(weight);  // Also update state for UI consistency
    onChangeFontStyle?.(style as 'normal' | 'italic' | 'oblique');  // Also update state for UI consistency

    console.log(`[EdgeDocsToolbar] Selected: ${family} (${weight}, ${style})`);
  };

  // Keyboard shortcuts handler (Ctrl+S for Save)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S (Mac) - Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        onSave?.();
        console.log('[EdgeDocsToolbar] Save triggered via keyboard shortcut');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSave]);

  return (
    <ToolbarProvider
      getSelection={getSelection}
      getDocumentState={getDocumentState}
    >
      <div
        className="relative z-40"
        style={{
          position: "fixed",
          top: 0,
          left: `${sidebarWidth}px`,
          right: 0,
          transition: "left 0.3s ease-in-out, width 0.3s ease-in-out",
        }}
      >
      {/* App Header: document title + menubar + share */}
      <div
        className="w-full bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700"
        style={{ height: APPBAR_H }}
      >
        <div className="flex h-full w-full items-start pr-2 pl-0 py-2">
          {/* Left: Document title with icon (Google Docs style) */}
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              {/* Document icon with WASM status indicator */}
              <div className="relative flex flex-col items-center">
                <FileTypeIcon fileType={documentFileType} size="lg" />
                {/* WASM Status Dot */}
                <div 
                  className="absolute -bottom-1 w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor: wasmStatus === 'ready' ? '#00AA00' : wasmStatus === 'error' ? '#FF0000' : '#FF8800'
                  }}
                  title={`WASM Typesetter: ${wasmStatus}`}
                />
              </div>

              <div className="flex items-baseline gap-2">
                {/* Primary: Document Title */}
                <span
                  className="text-sm font-medium text-gray-900 dark:text-slate-100"
                  title={documentTitle || "Document"}
                >
                  {documentTitle || "Untitled document"}
                </span>

                {/* Secondary: Version Number (chip) */}
                {versionNumber && (
                  <span
                    className="rounded bg-black/5 dark:bg-white/10 px-1.5 py-0.5 text-xs font-medium tabular-nums text-gray-700 dark:text-slate-200"
                    title={`Version ${versionNumber}`}
                  >
                    {versionNumber}
                  </span>
                )}

                {saveStatus && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-slate-300">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        saveStatus === 'saved'
                          ? 'bg-emerald-500'
                          : saveStatus === 'saving'
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`}
                    />
                    {saveStatus === 'saved' && 'Saved'}
                    {saveStatus === 'saving' && 'Saving...'}
                    {saveStatus === 'error' && 'Save failed'}
                  </span>
                )}
              </div>
            </div>
            {/* Menu items under document title - aligned with title text */}
            <nav 
              className="flex items-center gap-3 text-sm text-gray-700 dark:text-slate-200 mb-1"
              style={{ paddingLeft: TITLE_ICON_OFFSET_PX }}
            >
              <Menubar onOpenPageSetup={onOpenPageSetup} onSave={onSave} />
              {["Edit","View","Insert","Format","Tools","Extensions","Help"].map((m) => (
                <button
                  key={m}
                  className="rounded px-1 py-1 hover:bg-black/5 dark:hover:bg-white/10"
                  onClick={() => console.log(`${m} menu`)}
                >
                  {m}
                </button>
              ))}
            </nav>
          </div>

          {/* Right: Share + profile stub */}
          <div className="flex items-center gap-2 ml-auto">
            <button
              className="h-8 rounded-full bg-[#1a73e8] px-4 text-sm font-medium text-white hover:brightness-95"
              onClick={() => console.log("Share")}
            >
              Share
            </button>
            <div className="h-8 w-8 rounded-full bg-gray-300" aria-label="Profile" />
          </div>
        </div>
      </div>

      {/* Formatting Toolbar */}
      <div
        className="w-full bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700"
        style={{ height: TOOLBAR_H }}
      >
        <div className="flex h-full w-full items-center gap-1 pr-2 pl-0">
          {/* File Actions Group */}
          <div className="flex items-center gap-1">
            <IconButton label="Menus search">
              <img src={searchIcon} alt="" className="h-4 w-4 block" />
            </IconButton>
            <IconButton 
              label="Undo (Ctrl+Z)" 
              onClick={onUndo}
              disabled={!canUndo}
            >↶</IconButton>
            <IconButton 
              label="Redo (Ctrl+Y)" 
              onClick={onRedo}
              disabled={!canRedo}
            >↷</IconButton>
            <IconButton label="Print">
              <img src={printIcon} alt="" className="h-4 w-4 block" />
            </IconButton>
            <IconButton label="Paint format">
              <img src={paintIcon} alt="" className="h-4 w-4 block" />
            </IconButton>
          </div>

          <Divider />

          {/* View Controls */}
          <div className="flex items-center gap-1">
            <SmoothSelect
              value={
                zoomMode === 'fit' 
                  ? 'Fit' 
                  : zoomMode === 'fit-width'
                    ? 'Fit Width'
                    : `${Math.round(zoomValue * 100)}%`
              }
              onChange={(val) => {
                if (val === 'Fit') {
                  onChangeZoomMode('fit');
                } else if (val === 'Fit Width') {
                  onChangeZoomMode('fit-width');  // ✅ Enhancement 4: Handle fit-width mode
                } else {
                  onChangeZoomMode('custom');
                  onChangeZoomValue(parsePercent(val));
                }
              }}
              options={['Fit', 'Fit Width', ...ZOOMS]}  // ✅ Enhancement 4: Added 'Fit Width' option
              triggerWidth="w-[86px] sm:w-[100px]"
              ariaLabel="Zoom"
            />
          </div>

          <Divider />

          {/* Formatting Group */}
          <div className="flex items-center gap-1">
            <CascadingFontDropdown
              fontFamilies={fontFamilies}
              selectedFamily={fontFamilyKey}
              selectedWeight={selectedFontWeight}
              selectedStyle={selectedFontStyle}
              onSelect={handleFontSelect}
              disabled={fontsLoading}
            />
            <NudgeSize 
              fontSizePx={fontSizePx} 
              onChangeFontSize={onChangeFontSize}
              onChangeFontSizeWithRanges={onChangeFontSizeWithRanges}
              onPreviewFontSize={onPreviewFontSize}
              onCancelFontSizePreview={onCancelFontSizePreview}
            />
          </div>

          <Divider />

          {/* Text Formatting */}
          <div className="flex items-center gap-1">
            <IconToggle
              label="Bold"
              shortcut="Ctrl+B"
              isActive={isBold}
              onClick={onToggleBold}
            >
              B
            </IconToggle>
            <IconToggle
              label="Italic"
              shortcut="Ctrl+I"
              isActive={isItalic}
              onClick={onToggleItalic}
            >
              <span className="italic">I</span>
            </IconToggle>
            <IconToggle
              label="Underline"
              shortcut="Ctrl+U"
              isActive={isUnderline}
              onClick={onToggleUnderline}
            >
              <span className="underline">U</span>
            </IconToggle>
            <SplitDropdown label="Text color" onClick={() => console.log("Text color")} />
            <SplitDropdown label="Highlight"  onClick={() => console.log("Highlight")}  />
          </div>

          <Divider />

          {/* Insert Group */}
          <div className="flex items-center gap-1">
            <IconButton label="Insert link">🔗</IconButton>
            <IconButton label="Comment">💬</IconButton>
            <IconButton label="Insert image">🖼️</IconButton>
          </div>

          <Divider />

          {/* Alignment & Lists */}
          <div className="flex items-center gap-1">
            <IconToggle label="Align left">≡</IconToggle>
            <IconToggle label="Align center">≡</IconToggle>
            <IconToggle label="Align right">≡</IconToggle>
            <IconToggle label="Justify">≡</IconToggle>
            {/* 🔧 FIX (2026-01-25): Use LineSpacingDropdown for preview support */}
            {onChangeParagraphLineSpacingWithRanges && onPreviewLineSpacing ? (
              <LineSpacingDropdown
                value={lineHeightMul}
                onChange={onChangeParagraphLineSpacingWithRanges}
                onPreview={onPreviewLineSpacing}
                onCancel={onCancelLineSpacingPreview}
              />
            ) : (
              <ParagraphSpacing
                lineHeightMul={lineHeightMul}
                onChangeLineHeight={onChangeLineHeight}
                onChangeParagraphLineSpacing={onChangeParagraphLineSpacing}
              />
            )}
            <IconButton label="Bulleted list">• ●</IconButton>
            <IconButton label="Numbered list">1.</IconButton>
            <IconButton label="Decrease indent">⟵</IconButton>
            <IconButton label="Increase indent">⟶</IconButton>
          </div>

          <Divider />

          {/* Collaboration & Mode */}
          <div className="flex items-center gap-1 ml-auto">
            <IconButton label="Clear formatting" onClick={onClearFormatting}>✖</IconButton>
            <SmoothSelect
              value="Editing"
              onChange={(v) => console.log("Mode", v)}
              options={["Editing","Suggesting","Viewing"]}
              triggerWidth="w-[110px]"
              ariaLabel="Editing mode"
            />
          </div>
        </div>
      </div>

      {/* Ruler stub (reserved row) */}
      <div
        className="w-full bg-gray-100 dark:bg-slate-700 text-[11px] text-gray-500"
        style={{ height: RULER_H, lineHeight: `${RULER_H}px` }}
      >
        <div className="h-full w-full pr-2 pl-0">
          {/* TODO: ruler implementation will go here */}
        </div>
      </div>
      </div>
    </ToolbarProvider>
  );
}

/* ===== Helpers (kept local to the toolbar module) ===== */

function Divider() {
  return <div className="mx-1 h-4 w-px bg-gray-300 dark:bg-slate-600" aria-hidden="true" />;
}

function IconButton({ label, children, onClick, disabled }: { label: string; children: React.ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-7 min-w-7 items-center justify-center rounded px-1 text-[13px] hover:bg-black/5 dark:hover:bg-white/10 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

/**
 * IconToggle - Controlled toggle button for formatting actions
 *
 * @param label - Accessibility label (shown in title/tooltip)
 * @param children - Button content (e.g., "B" for bold)
 * @param isActive - Whether the button should appear pressed/active
 * @param onClick - Callback when button is clicked
 * @param shortcut - Optional keyboard shortcut hint (e.g., "Ctrl+B")
 */
function IconToggle({
  label,
  children,
  isActive = false,
  onClick,
  shortcut
}: {
  label: string;
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  shortcut?: string;
}) {
  const fullLabel = shortcut ? `${label} (${shortcut})` : label;

  // 🔧 FIX (2026-01-23): Use onMouseDown + preventDefault to prevent selection collapse
  // PROBLEM: When user selects text and clicks Bold button, the click event causes
  //          the browser to move focus to the button, which collapses the text selection.
  //          By the time onClick fires, selection.isCollapsed is true, so the code
  //          enters the "pending format" path instead of applying bold to selected text.
  // FIX: Use onMouseDown with preventDefault() to keep focus in the editor and preserve selection.
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent focus change and selection collapse
    onClick?.();
  };

  return (
    <button
      type="button"
      aria-pressed={isActive}
      title={fullLabel}
      onMouseDown={handleMouseDown}
      className="inline-flex h-7 min-w-7 items-center justify-center rounded px-1 text-[13px] hover:bg-black/5 dark:hover:bg-white/10 data-[on=true]:bg-lex-accent-soft data-[on=true]:text-lex-accent"
      data-on={isActive}
    >
      <span className={isActive ? "font-semibold" : ""}>{children}</span>
    </button>
  );
}


/**
 * NudgeSize - Font size control with +/- buttons and dropdown
 * 
 * 🆕 2026-01-24: Updated to support MS Word-like live preview
 * - Dropdown shows live preview on hover
 * - +/- buttons commit immediately (no preview needed)
 * - Selection is preserved during interaction
 */
function NudgeSize({
  fontSizePx,
  onChangeFontSize,
  onChangeFontSizeWithRanges,
  onPreviewFontSize,
  onCancelFontSizePreview,
}: { 
  fontSizePx: number; 
  onChangeFontSize: (v: number) => void;
  onChangeFontSizeWithRanges?: (v: number, ranges: ParagraphRange[]) => Promise<void>;
  onPreviewFontSize?: (v: number, ranges: ParagraphRange[]) => Promise<void>;
  onCancelFontSizePreview?: (originalV: number, ranges: ParagraphRange[]) => Promise<void>;
}) {
  const ctx = useToolbarContextSafe();

  // ✅ Enhancement 3: Expanded font size presets for more granular control
  const LIST = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

  // +/- buttons: bind selection, apply immediately, unbind
  const handleNudge = React.useCallback((newSize: number) => {
    const clampedSize = Math.max(8, Math.min(144, newSize));
    
    // If we have preview callbacks and context, use them
    if (ctx && onChangeFontSizeWithRanges) {
      ctx.bindSelection();
      const selection = ctx.callbacks?.getSelection();
      
      if (selection && !selection.isCollapsed) {
        const ranges = ctx.getParagraphRanges(selection);
        if (ranges.length > 0) {
          onChangeFontSizeWithRanges(clampedSize, ranges).finally(() => {
            ctx.unbindSelection();
          });
          return;
        }
      }
      ctx.unbindSelection();
    }
    
    // Fallback to legacy callback
    onChangeFontSize(clampedSize);
  }, [ctx, onChangeFontSize, onChangeFontSizeWithRanges]);

  const dec = () => {
    const idx = LIST.findIndex(v => v >= fontSizePx);
    if (idx > 0) handleNudge(LIST[idx - 1]);
    else handleNudge(fontSizePx - 1);
  };

  const inc = () => {
    const idx = LIST.findIndex(v => v > fontSizePx);
    if (idx >= 0) handleNudge(LIST[idx]);
    else handleNudge(fontSizePx + 1);
  };

  // Use new previewable dropdown if callbacks are provided
  const usePreviewDropdown = onChangeFontSizeWithRanges && onPreviewFontSize;

  // 🔬 DIAGNOSTIC: Log which props are received to identify missing callback
  React.useEffect(() => {
    console.log('[NudgeSize] Props check:', {
      hasOnChangeFontSizeWithRanges: !!onChangeFontSizeWithRanges,
      hasOnPreviewFontSize: !!onPreviewFontSize,
      hasOnCancelFontSizePreview: !!onCancelFontSizePreview,
      usePreviewDropdown,
      hasContext: !!ctx
    });
  }, [onChangeFontSizeWithRanges, onPreviewFontSize, onCancelFontSizePreview, ctx, usePreviewDropdown]);

  return (
    <div className="flex items-center">
      <IconButton label="Decrease font size" onClick={dec}>−</IconButton>
      {usePreviewDropdown ? (
        <FontSizeDropdown
          value={fontSizePx}
          onChange={onChangeFontSizeWithRanges}
          onPreview={onPreviewFontSize}
          onCancel={onCancelFontSizePreview}
        />
      ) : (
        <SmoothSelect
          value={String(fontSizePx)}
          onChange={(v) => onChangeFontSize(Math.max(8, Math.min(144, Number(v))))}
          options={LIST.map(String)}
          triggerWidth="w-[56px]"
          ariaLabel="Font size"
          menuFit
        />
      )}
      <IconButton label="Increase font size" onClick={inc}>＋</IconButton>
    </div>
  );
}

function SplitDropdown({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="flex">
      <button
        type="button"
        className="inline-flex h-7 items-center justify-center rounded-l px-2 text-[13px] text-gray-900 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
        title={label}
        onClick={onClick}
      >
        {label === "Text color" ? "A" : "▧"}
      </button>
      <button
        type="button"
        className="inline-flex h-7 items-center justify-center rounded-r border-l border-gray-200 dark:border-slate-600 px-1 text-[12px] text-gray-900 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
        title={`${label} menu`}
        onClick={() => console.log(`${label} menu`)}
      >
        ▾
      </button>
    </div>
  );
}

/**
 * Paragraph Spacing dropdown - MS Word style line spacing control
 * Presets: 1.0 (Single), 1.15, 1.5, 2.0 (Double), 2.5, 3.0
 *
 * 2026-01-19: Enhanced with per-paragraph line spacing support
 * - onChangeParagraphLineSpacing: Applies to selected/current paragraph(s)
 * - onChangeLineHeight: Fallback for document-level changes (new documents)
 */
function ParagraphSpacing({
  lineHeightMul,
  onChangeLineHeight,
  onChangeParagraphLineSpacing
}: {
  lineHeightMul: number;
  onChangeLineHeight?: (v: number) => void;
  onChangeParagraphLineSpacing?: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Line spacing presets (matching MS Word)
  const PRESETS = [
    { value: 1.0, label: '1.0', description: 'Single' },
    { value: 1.15, label: '1.15', description: '' },
    { value: 1.5, label: '1.5', description: '' },
    { value: 2.0, label: '2.0', description: 'Double' },
    { value: 2.5, label: '2.5', description: '' },
    { value: 3.0, label: '3.0', description: '' },
  ];

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (value: number) => {
    // Log line height change using DocumentLogger
    DocumentLogger.defaults.lineHeight({
      resolvedValue: value,
      source: 'userDefaults', // User explicitly changed via toolbar
      userDefaultsValue: value,
    });

    // 2026-01-19: Prefer paragraph-level changes (MS Word behavior)
    // If onChangeParagraphLineSpacing is provided, apply to selected paragraph(s)
    // Otherwise fall back to document-level change
    if (onChangeParagraphLineSpacing) {
      onChangeParagraphLineSpacing(value);
    } else {
      onChangeLineHeight?.(value);
    }
    setIsOpen(false);
  };

  // Find if current value matches a preset
  const currentPreset = PRESETS.find(p => Math.abs(p.value - lineHeightMul) < 0.01);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button with paragraph icon */}
      <button
        type="button"
        title="Line & paragraph spacing"
        aria-label="Line & paragraph spacing"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(v => !v)}
        className="inline-flex h-7 items-center justify-center gap-0.5 rounded px-1.5 text-[13px] text-gray-900 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
      >
        {/* Paragraph spacing icon (horizontal lines with arrows) */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="opacity-80">
          <rect x="2" y="3" width="12" height="1.5" rx="0.5" />
          <rect x="2" y="7.25" width="12" height="1.5" rx="0.5" />
          <rect x="2" y="11.5" width="12" height="1.5" rx="0.5" />
          {/* Up/down arrows on the right */}
          <path d="M14 5.5L15.5 7H12.5L14 5.5Z" />
          <path d="M14 10.5L15.5 9H12.5L14 10.5Z" />
        </svg>
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 top-full z-[1000] mt-1 w-48 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
          {/* Line spacing section */}
          <div className="py-1">
            {PRESETS.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleSelect(preset.value)}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 ${
                  currentPreset?.value === preset.value ? 'bg-lex-accent-soft' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  {/* Checkmark for selected */}
                  <span className="w-4">
                    {currentPreset?.value === preset.value && (
                      <span className="text-lex-accent">✓</span>
                    )}
                  </span>
                  <span className="text-gray-900 dark:text-slate-100">{preset.label}</span>
                  {preset.description && (
                    <span className="text-gray-500 dark:text-slate-400">({preset.description})</span>
                  )}
                </span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 dark:bg-slate-600" />

          {/* Additional options */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                console.log('[ParagraphSpacing] Custom line spacing options...');
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <span className="w-4" />
              <span>Line Spacing Options...</span>
            </button>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 dark:bg-slate-600" />

          {/* Space before/after paragraph */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                console.log('[ParagraphSpacing] Add space before paragraph');
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <span className="w-4" />
              <span>Add Space Before Paragraph</span>
            </button>
            <button
              type="button"
              onClick={() => {
                console.log('[ParagraphSpacing] Remove space after paragraph');
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <span className="w-4" />
              <span>Remove Space After Paragraph</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
